/* eslint css-modules/no-unused-class: [2, { markAsUsed: ['loader'] }] */
import { useRef } from 'react';
import { ParsedUrlQuery, ParsedUrlQueryInput } from 'querystring';
import { Form } from 'react-final-form';
import { UrlObject } from 'url';
import classNames from 'classnames';

import config from 'config';
import { successiveAliasRestFilters } from 'consts/filters';
import { ProductTypeResult } from 'graphql/queries/GetProductTypes.query';
import Link from '@components/atoms/Link';
import GlassLayer from '@templates/GlassLayer';
import Typography from '@components/atoms/Typography';
import ExpandArrow from '@components/atoms/ExpandArrow';
import Collapsible from '@components/Collapsible';
import RestItemsCollapsible from '@components/RestItemsCollapsible';
import { MappedFilter } from '@components/SearchController/SearchController.utils';
import pruneObject from 'utils/pruneObject';
import pick from 'utils/pick';
import { FormattedMessage } from 'utils/intl';
import Url from 'types/Url';

import FilterBox from '../FilterBox';
import { checkNestedFilters, getFormValues } from './FilterPanel.utils';
import messages from './FilterPanel.messages';
import classes from './FilterPanel.module.scss';

const { defaultMainLevelFilterNodesLimit, acceptableFilterNodesLimit } = config;

type Props = {
  filters: MappedFilter[];
  advancedFilters: MappedFilter[];
  productTypes?: ProductTypeResult[];
  numberOfCheckedFilters: number;
  initialValues: ParsedUrlQuery;
  loading: boolean;
  filterClearHref: Url;
  getHref: (newQuery: ParsedUrlQueryInput, excludeAliasQuery?: boolean | undefined) => UrlObject;
  getFilterChangeHref: (value: string[], key: string) => UrlObject;
  onSubmit: (values: Record<string, string[]>, clearRest?: boolean) => void;
};

const FilterPanel = ({
  filters,
  advancedFilters,
  productTypes,
  numberOfCheckedFilters,
  initialValues,
  loading,
  filterClearHref,
  getHref,
  getFilterChangeHref,
  onSubmit,
}: Props): JSX.Element => {
  const ref = useRef<HTMLFormElement>(null);

  const alwaysVisibleProductTypes = productTypes?.slice(0, acceptableFilterNodesLimit) || [];
  const restProductTypes = productTypes?.slice(acceptableFilterNodesLimit) || [];
  const areAdvancedFiltersOpened = checkNestedFilters(advancedFilters, initialValues);
  const isProductTypeFilterOpened = !!restProductTypes.find(({ name }) => initialValues.product_type === name);

  const mapProductType = (type: Exclude<Props['productTypes'], undefined>[0]) => (
    <Link
      key={type.id}
      component="div"
      className={classNames({ [classes.active]: initialValues.product_type === type.name })}
      href={getHref(
        pruneObject({
          ...pick(initialValues, [...successiveAliasRestFilters, 'manufacturer']),
          product_type: type.name,
        }),
        true
      )}
    >
      <Typography variant="body2" renderAs="span">
        {type.name}
      </Typography>
    </Link>
  );

  return (
    <div className={classes.wrapper}>
      {loading && <GlassLayer position="sticky" />}
      <div className={classes.headingWrapper}>
        <Typography variant="h2" display="block" className={classes.heading}>
          <FormattedMessage {...messages.heading} />
        </Typography>
        {!!numberOfCheckedFilters && (
          <Link component="div" href={filterClearHref} scroll>
            <Typography variant="body2" className={classes.clear}>
              <FormattedMessage values={{ quantity: numberOfCheckedFilters }} {...messages.clear} />
            </Typography>
          </Link>
        )}
      </div>
      <Form onSubmit={() => onSubmit(getFormValues(ref?.current?.elements))}>
        {({ handleSubmit }) => (
          <form ref={ref} onSubmit={handleSubmit}>
            <input type="submit" className={classes.submit} tabIndex={-1} />
            <div className={classes.list}>
              {!!productTypes?.length && (
                <div>
                  <Typography variant="h4" renderAs="div" weight="700">
                    <FormattedMessage {...messages.productType} />
                  </Typography>
                  <div className={classes.productTypes}>
                    {initialValues.product_type && (
                      <input type="hidden" name="product_type" value={initialValues.product_type} />
                    )}
                    {alwaysVisibleProductTypes.map(mapProductType)}
                    <RestItemsCollapsible
                      isInitiallyExpanded={isProductTypeFilterOpened}
                      itemsCount={restProductTypes.length}
                      classes={{ content: classes.restProductTypes }}
                    >
                      {restProductTypes.map(mapProductType)}
                    </RestItemsCollapsible>
                  </div>
                </div>
              )}
              {!!initialValues.product_type &&
                filters.map(({ key, name, values, nodes, isRange }) => (
                  <FilterBox
                    key={key}
                    title={name}
                    values={values}
                    nodes={nodes}
                    isRange={isRange}
                    initialValues={initialValues}
                    checkedNodes={[initialValues[key] || []].flat()}
                    mainLevelNodesLimit={defaultMainLevelFilterNodesLimit}
                    getFilterChangeHref={getFilterChangeHref}
                    onSubmit={handleSubmit}
                  />
                ))}
              {!!initialValues.product_type && !!advancedFilters.length && (
                <Collapsible
                  initiallyOpen={areAdvancedFiltersOpened}
                  trigger={(isOpened, handleClick) => (
                    <Typography
                      variant="h4"
                      renderAs="div"
                      weight={isOpened ? '700' : '400'}
                      className={classes.advancedFiltersHeader}
                      onClick={handleClick}
                    >
                      <FormattedMessage tagName="div" {...messages.advancedFilters} />
                      <ExpandArrow isOpened={isOpened} />
                    </Typography>
                  )}
                >
                  <div className={classes.advancedFilters}>
                    {advancedFilters.map(({ key, name, values, nodes, isRange }) => (
                      <FilterBox
                        key={key}
                        title={name}
                        values={values}
                        nodes={nodes}
                        isRange={isRange}
                        initialValues={initialValues}
                        checkedNodes={[initialValues[key] || []].flat()}
                        mainLevelNodesLimit={defaultMainLevelFilterNodesLimit}
                        getFilterChangeHref={getFilterChangeHref}
                        onSubmit={handleSubmit}
                      />
                    ))}
                  </div>
                </Collapsible>
              )}
            </div>
          </form>
        )}
      </Form>
    </div>
  );
};

export default FilterPanel;
